<template>
  <article class="flex items-start gap-3 rounded-2xl border border-main bg-surface p-4" data-testid="notification-card">
    <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-elevation-2">
      <component :is="categoryIcon" class="lucide-icon-s text-subtle" aria-hidden="true" data-testid="notification-icon" />
    </span>

    <div class="flex min-w-0 grow flex-col gap-1.5">
      <div v-if="tagBlocks.length" class="flex flex-wrap items-center gap-2">
        <NotificationCardTagBlock
          v-for="(block, idx) in tagBlocks"
          :key="`${block.type}-${idx}`"
          :block="block"
          :data-testid="`notification-tag-${tagSlug(block.type)}`"
        />
      </div>

      <div v-if="isUnread || headerBlock" class="flex items-center gap-2">
        <span
          v-if="isUnread"
          class="inline-block size-2 shrink-0 rounded-full bg-primary-main"
          data-testid="notification-unread-dot"
          role="status"
          :aria-label="t('Dashboard.notifications.notificationCard.unread')"
        />
        <NotificationCardHeaderBlock v-if="headerBlock" :block="headerBlock" />
      </div>

      <NotificationCardSubheadlineBlock v-if="subheadlineBlock" :block="subheadlineBlock" />

      <div v-if="ctaBlocks.length" class="mt-0.5 flex flex-wrap items-center gap-4">
        <NotificationCardCtaBlock v-for="(block, idx) in ctaBlocks" :key="idx" :block="block" @click="emit('ctaClick', notification.id)" />
      </div>
    </div>

    <div class="flex shrink-0 flex-col items-end justify-between gap-3 self-stretch">
      <div class="flex items-center gap-1">
        <IconButton
          v-if="showReadToggle"
          v-tooltip="{ content: readToggleLabel, placement: 'left-start' }"
          size="extra-small"
          variant="outline-neutral"
          :icon="isUnread ? MailOpenIcon : MailIcon"
          :aria-label="readToggleLabel"
          data-testid="notification-read-toggle"
          @click="emit('toggleRead', notification.id)"
        />
        <IconButton
          v-if="showRestore"
          v-tooltip="{ content: t('NotificationCenter.card.restore'), placement: 'left-start' }"
          size="extra-small"
          variant="outline-neutral"
          :icon="ArchiveRestoreIcon"
          :aria-label="t('NotificationCenter.card.restore')"
          data-testid="notification-restore"
          @click="emit('restore', notification.id)"
        />
        <IconButton
          v-else-if="showArchive"
          v-tooltip="{ content: t('NotificationCenter.card.archive'), placement: 'left-start' }"
          size="extra-small"
          variant="outline-neutral"
          :icon="ArchiveIcon"
          :aria-label="t('NotificationCenter.card.archive')"
          data-testid="notification-archive"
          @click="emit('archive', notification.id)"
        />
      </div>

      <time :datetime="notification.createdAt" class="txt-label-s-regular text-subtlest" data-testid="notification-timestamp">
        {{ timestamp }}
      </time>
    </div>
  </article>
</template>

<script setup lang="ts">
import { IconButton } from '@ramp106/omrjs-core-ui'
import { ArchiveIcon, ArchiveRestoreIcon, MailIcon, MailOpenIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatNotificationTimestamp } from '@/helpers/formatNotificationTimestamp'

import NotificationCardCtaBlock from './NotificationCardCtaBlock.vue'
import NotificationCardHeaderBlock from './NotificationCardHeaderBlock.vue'
import NotificationCardSubheadlineBlock from './NotificationCardSubheadlineBlock.vue'
import NotificationCardTagBlock from './NotificationCardTagBlock.vue'
import { notificationCategoryIcon } from './notificationCategoryIcon'
import type { NotificationNode } from './types'

const { t, locale } = useI18n()

const props = withDefaults(
  defineProps<{
    notification: NotificationNode
    showReadToggle?: boolean
    showRestore?: boolean
    showArchive?: boolean
  }>(),
  { showReadToggle: false, showRestore: false, showArchive: true },
)

const emit = defineEmits<{ archive: [id: string]; ctaClick: [id: string]; toggleRead: [id: string]; restore: [id: string] }>()

const TAG_PREFIX = 'tag.'

function tagSlug(tagType: string): string {
  return tagType.startsWith(TAG_PREFIX) ? tagType.slice(TAG_PREFIX.length) : tagType
}

const isUnread = computed(() => !props.notification.readAt)
const readToggleLabel = computed(() => t(isUnread.value ? 'NotificationCenter.card.markAsRead' : 'NotificationCenter.card.markAsUnread'))
const categoryIcon = computed(() => notificationCategoryIcon(props.notification.category))
const tagBlocks = computed(() => props.notification.content.filter((block) => block.type.startsWith(TAG_PREFIX)))
const headerBlock = computed(() => props.notification.content.find((block) => block.type === 'header'))
const subheadlineBlock = computed(() => props.notification.content.find((block) => block.type === 'subheadline'))
const ctaBlocks = computed(() => props.notification.content.filter((block) => block.type === 'cta'))
const timestamp = computed(() => formatNotificationTimestamp(props.notification.createdAt, { locale: locale.value }))
</script>
